import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  Image, Loader2, Check, Sparkles, Globe, 
  Type, Link2, Target, DollarSign, Users,
  ChevronDown, Instagram, Facebook, Upload, X, ImagePlus
} from 'lucide-react';

const CTA_OPTIONS = [
  { value: 'LEARN_MORE', label: 'Daha Fazla Bilgi' },
  { value: 'SHOP_NOW', label: 'Şimdi Alışveriş Yap' },
  { value: 'SIGN_UP', label: 'Kayıt Ol' },
  { value: 'CONTACT_US', label: 'Bize Ulaşın' },
  { value: 'GET_QUOTE', label: 'Teklif Al' },
  { value: 'BOOK_NOW', label: 'Şimdi Rezervasyon Yap' },
  { value: 'DOWNLOAD', label: 'İndir' },
  { value: 'WATCH_MORE', label: 'Daha Fazla İzle' },
];

const OBJECTIVE_OPTIONS = [
  { value: 'OUTCOME_TRAFFIC', label: 'Trafik', desc: 'Web sitenize ziyaretçi çekin' },
  { value: 'OUTCOME_SALES', label: 'Satış', desc: 'Dönüşüm ve satış odaklı' },
  { value: 'OUTCOME_LEADS', label: 'Potansiyel Müşteri', desc: 'Form doldurma ve lead toplama' },
];

export default function MetaImageCampaignLab() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Connections
  const [activePage, setActivePage] = useState(null);
  const [activeInstagram, setActiveInstagram] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  
  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [objective, setObjective] = useState('OUTCOME_TRAFFIC');
  const [dailyBudget, setDailyBudget] = useState('100');
  const [imageUrl, setImageUrl] = useState('');
  const [headline, setHeadline] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [ctaType, setCtaType] = useState('LEARN_MORE');
  
  // Image upload state
  const [imageMode, setImageMode] = useState('upload'); // 'upload' or 'url'
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Targeting
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [selectedGender, setSelectedGender] = useState('all'); // 'all', 'male', 'female'
  
  // Result
  const [createdResult, setCreatedResult] = useState(null);

  // Load connections
  useEffect(() => {
    if (selectedBrand?.id) {
      resolveConnections();
    }
  }, [selectedBrand?.id]);

  const resolveConnections = async () => {
    if (!selectedBrand?.id) return;
    setConnectionLoading(true);
    
    try {
      const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      const data = await resp.json();
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      
      const facebookPageConn = connections.find((c) => c?.platform === 'FACEBOOK_PAGE' && c?.status === 'ACTIVE');
      const instagramConn = connections.find((c) => c?.platform === 'INSTAGRAM' && c?.status === 'ACTIVE');
      
      if (facebookPageConn) {
        setActivePage({
          id: facebookPageConn.customerId,
          name: facebookPageConn.accountName || 'Facebook Page'
        });
      }

      if (instagramConn) {
        setActiveInstagram({
          id: instagramConn.customerId,
          name: instagramConn.accountName || 'Instagram Account'
        });
      }
    } catch (e) {
      console.error('Failed to resolve connections', e);
    } finally {
      setConnectionLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Process the selected file
  const processFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }
    
    // Validate file size (max 30MB for Meta)
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Dosya boyutu 30MB\'dan küçük olmalı');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove selected file
  const removeFile = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    if (!selectedBrand?.id) {
      toast.error('Lütfen bir marka seçin');
      return;
    }
    if (!activePage?.id) {
      toast.error('Facebook Page bağlantısı gerekli');
      return;
    }
    
    // Check image based on mode
    const hasImage = imageMode === 'upload' ? !!imageFile : !!imageUrl;
    if (!hasImage || !headline || !bodyText || !linkUrl) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    if (!dailyBudget || parseFloat(dailyBudget) < 10) {
      toast.error('Minimum günlük bütçe 10 TL');
      return;
    }

    setLoading(true);
    setCreatedResult(null);

    try {
      const genders = selectedGender === 'male' ? [1] : selectedGender === 'female' ? [2] : [];
      
      // Use FormData for file upload support
      const formData = new FormData();
      formData.append('campaignName', campaignName || '');
      formData.append('objective', objective);
      formData.append('dailyBudget', parseFloat(dailyBudget));
      formData.append('countries', JSON.stringify(['TR']));
      formData.append('ageMin', parseInt(ageMin));
      formData.append('ageMax', parseInt(ageMax));
      formData.append('genders', JSON.stringify(genders));
      formData.append('headline', headline);
      formData.append('bodyText', bodyText);
      formData.append('description', description);
      formData.append('linkUrl', linkUrl);
      formData.append('ctaType', ctaType);
      formData.append('optimizationGoal', objective === 'OUTCOME_TRAFFIC' ? 'LINK_CLICKS' : 'CONVERSIONS');
      formData.append('pageId', activePage.id);
      formData.append('instagramId', activeInstagram?.id || '');
      
      // Add image - either file or URL
      if (imageMode === 'upload' && imageFile) {
        formData.append('imageFile', imageFile);
      } else if (imageUrl) {
        formData.append('imageUrl', imageUrl);
      }

      const res = await fetch('https://api.glowytics.com/api/labs/meta-ads/create-single-image-campaign', {
        method: 'POST',
        headers: {
          'X-Brand-ID': selectedBrand.id
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData,
        credentials: 'include'
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details?.error || 'Kampanya oluşturulamadı');

      if (data.success) {
        setCreatedResult(data);
        toast.success('Kampanya başarıyla oluşturuldu!');
      } else {
        throw new Error(data.error || 'Kampanya oluşturulamadı');
      }
    } catch (e) {
      console.error('Campaign creation error:', e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedBrand?.id) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-800">
          <p className="font-medium">Lütfen sol menüden bir marka seçin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Image className="w-8 h-8 text-violet-600" />
          Tek Görselli Kampanya Oluştur
        </h1>
        <p className="text-gray-500 mt-2 text-lg">
          Tek bir görsel ile hızlıca Meta reklam kampanyası oluşturun.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="p-8 space-y-8">
          
          {/* Connected Accounts */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Bağlı Hesaplar</h3>
            
            {connectionLoading ? (
              <div className="flex items-center gap-2 text-gray-400 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Bağlantılar yükleniyor...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                  activePage ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
                }`}>
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border shadow-sm">
                    <Facebook className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Facebook Page</p>
                    <p className="font-semibold text-gray-900 truncate">
                      {activePage?.name || 'Bağlı değil'}
                    </p>
                  </div>
                  {activePage && <Check className="w-5 h-5 text-green-600" />}
                </div>

                <div className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                  activeInstagram ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border shadow-sm">
                    <Instagram className="w-5 h-5 text-pink-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Instagram (Opsiyonel)</p>
                    <p className="font-semibold text-gray-900 truncate">
                      {activeInstagram?.name || 'Bağlı değil'}
                    </p>
                  </div>
                  {activeInstagram && <Check className="w-5 h-5 text-green-600" />}
                </div>
              </div>
            )}
          </div>

          {/* Campaign Settings */}
          <div className="space-y-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <Target className="w-4 h-4" />
              Kampanya Ayarları
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kampanya Adı <span className="text-gray-400">(opsiyonel)</span>
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Glowytics Kampanya"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Günlük Bütçe (TL)
                </label>
                <input
                  type="number"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(e.target.value)}
                  min="10"
                  step="10"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya Hedefi</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {OBJECTIVE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setObjective(opt.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      objective === opt.value 
                        ? 'border-violet-500 bg-violet-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Targeting */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" />
              Hedefleme
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yaş Aralığı</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                    min="18"
                    max="65"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-center"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                    min="18"
                    max="65"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-center"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cinsiyet</label>
                <select
                  value={selectedGender}
                  onChange={(e) => setSelectedGender(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white"
                >
                  <option value="all">Tümü</option>
                  <option value="male">Erkek</option>
                  <option value="female">Kadın</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ülke</label>
                <div className="px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600">
                  🇹🇷 Türkiye
                </div>
              </div>
            </div>
          </div>

          {/* Creative */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Reklam Kreatifi
            </h3>

            {/* Image Mode Toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
              <button
                onClick={() => setImageMode('upload')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  imageMode === 'upload' 
                    ? 'bg-white text-violet-700 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Upload className="w-4 h-4" />
                Görsel Yükle
              </button>
              <button
                onClick={() => setImageMode('url')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  imageMode === 'url' 
                    ? 'bg-white text-violet-700 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Link2 className="w-4 h-4" />
                URL Gir
              </button>
            </div>

            {/* Upload Mode */}
            {imageMode === 'upload' && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {!imageFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/30 transition-all"
                  >
                    <ImagePlus className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium">Görsel yüklemek için tıklayın veya sürükleyin</p>
                    <p className="text-xs text-gray-400 mt-2">PNG, JPG, WEBP • Max 30MB • Önerilen: 1200x628 px</p>
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover"
                    />
                    <button
                      onClick={removeFile}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <p className="text-white text-sm font-medium truncate">{imageFile.name}</p>
                      <p className="text-white/70 text-xs">{(imageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* URL Mode */}
            {imageMode === 'url' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Görsel URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-1">1200x628 px önerilen boyut (1.91:1 oran)</p>
                </div>

                {imageUrl && (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
                    <img 
                      src={imageUrl} 
                      alt="Preview" 
                      className="w-full h-48 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Type className="w-4 h-4 inline mr-1" />
                  Başlık <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Dikkat çekici başlık"
                  maxLength={40}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-400 mt-1">{headline.length}/40 karakter</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Link2 className="w-4 h-4 inline mr-1" />
                  Landing Page URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://yoursite.com/landing"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reklam Metni <span className="text-red-500">*</span>
              </label>
              <textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Reklamınızın ana metni..."
                rows={3}
                maxLength={125}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{bodyText.length}/125 karakter</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama <span className="text-gray-400">(opsiyonel)</span>
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Link altı açıklama"
                  maxLength={30}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aksiyon Butonu</label>
                <select
                  value={ctaType}
                  onChange={(e) => setCtaType(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  {CTA_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Create Button */}
          <div className="pt-4 border-t border-gray-100">
            <button
              onClick={handleCreate}
              disabled={loading || !activePage}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 ${
                loading || !activePage
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Kampanya Oluşturuluyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Kampanyayı Oluştur
                </>
              )}
            </button>
            
            {!activePage && !connectionLoading && (
              <p className="text-center text-sm text-red-500 mt-2">
                Facebook Page bağlantısı gerekli. Lütfen önce bağlantı kurun.
              </p>
            )}
          </div>

          {/* Success Result */}
          {createdResult && createdResult.success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 space-y-3">
              <div className="flex items-center gap-2 text-green-800">
                <Check className="w-6 h-6" />
                <h3 className="font-bold text-lg">Kampanya Başarıyla Oluşturuldu!</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Kampanya ID</p>
                  <p className="font-mono text-gray-900">{createdResult.campaign_id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Kampanya Adı</p>
                  <p className="font-semibold text-gray-900">{createdResult.campaign_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Günlük Bütçe</p>
                  <p className="font-semibold text-gray-900">{createdResult.daily_budget} TL</p>
                </div>
                <div>
                  <p className="text-gray-500">Yerleşimler</p>
                  <p className="font-semibold text-gray-900">{createdResult.placements}</p>
                </div>
              </div>
              <p className="text-green-700 text-sm mt-2">
                ⚠️ Kampanya <strong>PAUSED</strong> durumunda oluşturuldu. Meta Ads Manager'dan aktif edebilirsiniz.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

